/**
 * Heuristic IntentClusterer — clusters demand signals into CustomerIntents
 * by shared significant tokens. No LLM. Good enough for the smallest working
 * engine; the LLM-backed clusterer will slot in behind the same interface.
 */

import type { CustomerIntent, DemandDetail, EvidenceSignal } from "./types";

const STOPWORDS = new Set([
  "the", "a", "an", "and", "or", "for", "of", "to", "in", "on", "with",
  "at", "by", "from", "is", "are", "how", "what", "best", "top", "near",
  "me", "my", "your", "you", "buy", "get", "shop",
]);

function tokens(s: string): string[] {
  return s
    .toLowerCase()
    .replace(/[^a-z0-9\s]/g, " ")
    .split(/\s+/)
    .filter((t) => t.length > 3 && !STOPWORDS.has(t));
}

function stem(t: string): string {
  // extremely small stemmer: strip common plural/suffix noise
  return t.replace(/(ies|es|s|ing|ed)$/g, "");
}

function keyOf(s: string): string {
  const stems = tokens(s).map(stem).sort();
  return stems.join("-") || s.toLowerCase();
}

function describe(label: string): string {
  // Very light rewrite from keyword-shape to intent-shape.
  return `Customers looking for ${label.toLowerCase()}.`;
}

function labelize(subject: string): string {
  return subject
    .split(/\s+/)
    .map((w) => (w.length > 2 ? w[0].toUpperCase() + w.slice(1) : w))
    .join(" ");
}

/**
 * Group demand signals whose stemmed token bags overlap into one intent.
 * The signal with the highest demand becomes the intent's canonical label.
 */
export function clusterIntents(
  signals: EvidenceSignal[],
): CustomerIntent[] {
  const demand = signals.filter(
    (s) =>
      s.kind === "demand.search_term" ||
      s.kind === "demand.question" ||
      s.kind === "demand.ai_prompt",
  ) as EvidenceSignal<DemandDetail>[];

  // First pass: bucket by exact token-stem key.
  const buckets = new Map<string, EvidenceSignal<DemandDetail>[]>();
  for (const s of demand) {
    const k = keyOf(s.subject);
    if (!buckets.has(k)) buckets.set(k, []);
    buckets.get(k)!.push(s);
  }

  // Second pass: merge buckets that share any significant stem.
  const bucketList = Array.from(buckets.entries()).map(([k, items]) => ({
    keys: new Set(k.split("-").filter(Boolean)),
    items,
  }));

  const merged: typeof bucketList = [];
  for (const b of bucketList) {
    const target = merged.find((m) => {
      for (const k of b.keys) if (m.keys.has(k)) return true;
      return false;
    });
    if (target) {
      for (const k of b.keys) target.keys.add(k);
      target.items.push(...b.items);
    } else {
      merged.push({ keys: new Set(b.keys), items: [...b.items] });
    }
  }

  return merged.map((bucket) => {
    const items = bucket.items;
    // Pick canonical by highest volume, then highest local_confidence.
    const canonical = [...items].sort((a, b) => {
      const av = a.detail.monthly_volume ?? a.detail.volume_index ?? 0;
      const bv = b.detail.monthly_volume ?? b.detail.volume_index ?? 0;
      if (bv !== av) return bv - av;
      return (b.detail.local_confidence ?? 0) - (a.detail.local_confidence ?? 0);
    })[0];

    const label = labelize(canonical.subject);
    const totalVol = items.reduce(
      (acc, s) => acc + (s.detail.monthly_volume ?? s.detail.volume_index ?? 0),
      0,
    );
    // Normalize demand strength to 0..1 via log scale (200/mo ~ 1.0).
    const demand_strength = Math.min(1, Math.log10(totalVol + 1) / Math.log10(201));

    const rising = items.filter((s) => s.detail.trend === "rising").length;
    const falling = items.filter((s) => s.detail.trend === "falling").length;
    const trend: CustomerIntent["trend"] =
      rising > falling ? "rising" : falling > rising ? "falling" : "flat";

    const local_confidence =
      items.reduce((acc, s) => acc + (s.detail.local_confidence ?? 0.5), 0) /
      items.length;

    return {
      intent_id: `intent:${keyOf(canonical.subject)}`,
      label,
      description: describe(label),
      evidence: items.map((s) => s.signal_id),
      demand_strength,
      trend,
      local_confidence,
      tags: Array.from(bucket.keys),
    };
  });
}